import { useState, useEffect } from "react";
import {
  Button,
  Grid,
  CircularProgress,
  Autocomplete,
  TextField,
  Checkbox,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";

import { getLawyers } from "../../services/LawyerService";
import { exportMunicipalitySheet } from "../../services/LiquidationService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import LoadingState from "../General/LoadingState";
import LegalFileStageFilter from "../General/LegalFileStageFilter";
import LegalFileTypeFilter from "../General/LegalFileTypeFilter";
import LegalFileStatusTaxFilter from "../General/LegalFileStatusTaxFilter";
import FileUploadBox from "../General/FileUploadBox";
import DynamicExcelHelpModal from "../General/DynamicExcelHelpModal";
import { useToast } from "../../context/ToastContext";

export default function ReportMunicipalitySheet() {
  const { showToast } = useToast();
  const theme = useTheme();
  const [mode, setMode] = useState("filtro"); // filtro | archivo
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedStatusTax, setSelectedStatusTax] = useState([]);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [openHelp, setOpenHelp] = useState(false);

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const handleModeChange = (event, newMode) => {
    if (newMode) setMode(newMode);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    const validExtensions = ["xls", "xlsx", "csv"];
    const fileExtension = file.name.split(".").pop().toLowerCase();
    if (!validExtensions.includes(fileExtension)) {
      setFileError("Solo se permiten archivos .xls, .xlsx o .csv");
      setSelectedFile(null);
      return;
    }
    setFileError("");
    setSelectedFile(file);
  };

  useEffect(() => {
    async function fetchLawyers() {
      setLoadingOptions(true);
      try {
        const res = await getLawyers();
        setLawyers(res.data.data || []);
      } catch (error) {
        console.error("Error al cargar abogados:", error);
        showToast("Error al cargar abogados.", "error");
      } finally {
        setLoadingOptions(false);
      }
    }
    fetchLawyers();
  }, [showToast]);

  const handleGenerateReport = async () => {
    if (mode === "archivo" && !selectedFile) {
      showToast("Debe seleccionar un archivo.", "info");
      return;
    }

    if (
      mode === "filtro" &&
      startDate &&
      endDate &&
      startDate.isAfter(endDate)
    ) {
      showToast(
        "La fecha de inicio no puede ser posterior a la fecha de fin.",
        "error",
      );
      return;
    }

    setLoading(true);
    try {
      let payload;

      if (mode === "archivo") {
        payload = new FormData();
        payload.append("file", selectedFile);
      } else {
        payload = {
          lawyers: selectedLawyers,
          legal_file_stage_ids: selectedStages.map((s) => s.id),
          legal_file_types: selectedTypes.map((t) => t.id),
          status_tax_ids: selectedStatusTax.map((s) => s.id),
        };

        if (startDate) payload.start_date = startDate.format("YYYY-MM-DD");
        if (endDate) payload.end_date = endDate.format("YYYY-MM-DD");
      }

      const response = await exportMunicipalitySheet(payload);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const now = new Date();
      const formattedDate = now
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-")
        .replace("T", "_");

      a.download = `planilla_municipalidad_${formattedDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast("Ocurrió un error al generar la planilla.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return <LoadingState text="Cargando filtros..." />;
  }

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <GlassCard>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Tooltip title="Ver requisitos del Excel" arrow>
              <span>
                <HelpOutlineIcon
                  sx={{
                    cursor: mode === "archivo" ? "pointer" : "default",
                    opacity: mode === "archivo" ? 1 : 0.3,
                    color: theme.palette.primary.main,
                    "&:hover":
                      mode === "archivo"
                        ? { color: theme.palette.primary.dark }
                        : undefined,
                  }}
                  onClick={() => mode === "archivo" && setOpenHelp(true)}
                />
              </span>
            </Tooltip>
          </Box>

          <CardHeader
            icon={<InsertDriveFileRoundedIcon />}
            title="Planilla Municipalidad"
            description="Expedientes con sentencia (fecha tomada de las operaciones configuradas como marcadores de sentencia municipalidad), con los honorarios de sentencia/art. 104 y de ejecución de la liquidación activa."
          />

          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={handleModeChange}
              color="primary"
            >
              <ToggleButton value="filtro">
                <FilterAltRoundedIcon sx={{ mr: 1 }} fontSize="small" />
                Por Filtros
              </ToggleButton>
              <ToggleButton value="archivo">
                <UploadFileRoundedIcon sx={{ mr: 1 }} fontSize="small" />
                Por Archivo
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {mode === "archivo" ? (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FileUploadBox
                  selectedFile={selectedFile}
                  onFileSelect={handleFileSelect}
                  accept=".xls,.xlsx,.csv"
                  placeholder="Seleccionar Excel de Expedientes"
                  helperText="O arrastrá el archivo Excel aquí (.xls, .xlsx, .csv)"
                />
                {fileError && (
                  <Typography
                    color="error"
                    variant="caption"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {fileError}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleGenerateReport}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Generar Planilla"
                  )}
                </Button>
              </Grid>
            </Grid>
          ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
              <DatePicker
                label="Sentencia desde"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
              <DatePicker
                label="Sentencia hasta"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <LegalFileStageFilter
                value={selectedStages}
                onChange={setSelectedStages}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <LegalFileTypeFilter
                value={selectedTypes}
                onChange={setSelectedTypes}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <LegalFileStatusTaxFilter
                value={selectedStatusTax}
                onChange={setSelectedStatusTax}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Autocomplete
                multiple
                options={[
                  { id: "select-all", name: "Seleccionar todos", lastname: "" },
                  ...lawyers,
                ]}
                disableCloseOnSelect
                getOptionLabel={(option) =>
                  option.id === "select-all"
                    ? option.name
                    : `${option.name} ${option.lastname}` || option
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedLawyers}
                onChange={(event, newValue) => {
                  const selectAllOption = newValue.find(
                    (val) => val.id === "select-all",
                  );
                  if (selectAllOption) {
                    if (selectedLawyers.length === lawyers.length) {
                      setSelectedLawyers([]);
                    } else {
                      setSelectedLawyers(lawyers);
                    }
                  } else {
                    setSelectedLawyers(newValue);
                  }
                }}
                renderOption={(props, option, { selected }) => {
                  const isSelectAll = option.id === "select-all";
                  const allSelected = selectedLawyers.length === lawyers.length;
                  return (
                    <li {...props}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={isSelectAll ? allSelected : selected}
                      />
                      {isSelectAll
                        ? option.name
                        : `${option.name} ${option.lastname}` || option}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Abogados"
                    placeholder="Seleccionar..."
                  />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleGenerateReport}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Generar Planilla"
                )}
              </Button>
            </Grid>
          </Grid>
          )}
        </GlassCard>
      </Grid>

      <DynamicExcelHelpModal
        open={openHelp}
        onClose={() => setOpenHelp(false)}
        title="Requisitos del archivo Excel"
        description="El archivo debe ser formato .xls, .xlsx o .csv."
        columns={["Código de Expediente"]}
        example={["12345678"]}
      />
    </Grid>
  );
}
